import { currencyFormatterHelpers } from '@/utils/currencyFormatter';

const {
  formatCurrencyNumber: formatNumberWithCommas,
  currencySupportsDecimal,
  normalizeCurrencyDecimals,
} = currencyFormatterHelpers;

/**
 * Checks if an offer salary is above the requisition's salary range
 * @param offerSalary - The offer salary (can be string or number)
 * @param maxSalary - The maximum salary from the requisition
 * @returns true if the offer salary exceeds the max salary
 *
 * @example
 * isSalaryAboveRange(100000, 80000) // Returns: true
 * isSalaryAboveRange("75000.00", 80000) // Returns: false
 */
export const isSalaryAboveRange = (
  offerSalary?: string | number | null,
  maxSalary?: number | null
): boolean => {
  if (!offerSalary || maxSalary === undefined || maxSalary === null) {
    return false;
  }

  const offerSalaryNum = parseFloat(offerSalary.toString());
  return !isNaN(offerSalaryNum) && offerSalaryNum > maxSalary;
};

/**
 * Formats a salary range for display
 * @param minSalary - The minimum salary
 * @param maxSalary - The maximum salary
 * @param currency - The currency code
 * @returns A formatted salary range string
 *
 * @example
 * formatSalaryRange(50000, 80000, "USD") // Returns: "$50,000 - $80,000 USD"
 */
const getCurrencySymbol = (code: string): string => {
  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value || code;
  } catch {
    return code;
  }
};

export const formatSalaryRange = (
  minSalary: number,
  maxSalary: number,
  currency: string
): string => {
  const symbol = getCurrencySymbol(currency);
  // Preserve decimals for comma-separator currencies so the range matches the
  // saved value; full-stop currencies stay integer-only (ENG-3539).
  const preserveDecimals = currencySupportsDecimal(currency);
  const formattedMin = formatNumberWithCommas(
    minSalary,
    currency,
    true,
    preserveDecimals
  );
  const formattedMax = formatNumberWithCommas(
    maxSalary,
    currency,
    true,
    preserveDecimals
  );
  return `${symbol}${formattedMin} - ${symbol}${formattedMax} ${currency}`;
};

/**
 * Formats salary for display in offer details
 * @param salary - The salary amount (can be string or number)
 * @param currency - The currency object with currency code
 * @returns A formatted string like "50000 (USD)" or "-" if no salary
 *
 * @example
 * formatOfferSalary("85000.75", { id: 1, currency: "USD" })
 * // Returns: "85,000.75 (USD)"
 *
 * formatOfferSalary("50000.00", { id: 1, currency_code: "EUR" })
 * // Returns: "50.000 (EUR)"  — full-stop currencies stay integer-only
 */
export const formatOfferSalary = (
  salary?: string | number | null,
  currency?: {
    id?: number;
    currency?: string;
    currency_code?: string;
  }
): string => {
  if (!salary) return '-';

  const currencyCode = currency?.currency || currency?.currency_code;
  const currencyDisplay =
    currency?.id && currencyCode ? ` (${currencyCode})` : '';

  // Preserve decimals for comma-separator currencies so the card matches the
  // saved value; full-stop currencies stay integer-only (ENG-3539).
  const preserveDecimals = currencySupportsDecimal(currencyCode);
  const salaryValue = preserveDecimals
    ? normalizeCurrencyDecimals(salary)
    : typeof salary === 'string'
      ? salary.split('.')?.[0]
      : Math.floor(salary).toString();

  const formattedSalary = formatNumberWithCommas(
    salaryValue,
    currencyCode,
    true,
    preserveDecimals
  );

  return `${formattedSalary}${currencyDisplay}`;
};

/**
 * Constructs a formatted equity label from equity award details
 * @param ats_equity_award - The equity award object containing vesting and amount details
 * @returns A formatted string describing the equity award or undefined if no award
 *
 * @example
 * constructEquityLabel({
 *   amount: "10000",
 *   currency: { id: 1, currency: "USD" },
 *   cliff_of_vesting: "1",
 *   unit_of_cliff: "year",
 *   cycles_after_cliff: "12",
 *   unit_of_cycle_after_cliff: "month",
 *   period_of_vesting: "4",
 *   unit_of_vesting_period: "year"
 * })
 * // Returns: "10000 USD, 1 year cliff, 12 months post-cliff cycle, 4 years vesting period"
 */
export const constructEquityLabel = (ats_equity_award?: {
  amount?: string;
  cliff_of_vesting?: string;
  unit_of_cliff?: string;
  period_of_vesting?: string;
  unit_of_vesting_period?: string;
  cycles_after_cliff?: string;
  currency?: any;
  unit_of_cycle_after_cliff?: string;
}): string | undefined => {
  if (!ats_equity_award) return undefined;

  const {
    amount,
    currency,
    cliff_of_vesting,
    unit_of_cliff,
    period_of_vesting,
    unit_of_vesting_period,
    cycles_after_cliff,
    unit_of_cycle_after_cliff,
  } = ats_equity_award;

  const parts: string[] = [];

  // Format the amount/units if available
  if (amount) {
    const value = amount
      ? `${amount?.split('.')?.[0]} ${
          currency?.id ? currency?.currency : 'Units'
        }`
      : '';
    parts.push(value);
  }

  // Cliff vesting period
  if (cliff_of_vesting) {
    const unitCliff =
      unit_of_cliff && parseInt(cliff_of_vesting) > 1
        ? `${unit_of_cliff}s`
        : unit_of_cliff;
    parts.push(`${cliff_of_vesting} ${unitCliff || ''} cliff`);
  }

  // Post-cliff cycles
  if (cycles_after_cliff) {
    const unitCycle =
      unit_of_cycle_after_cliff && parseInt(cycles_after_cliff) > 1
        ? `${unit_of_cycle_after_cliff}s`
        : unit_of_cycle_after_cliff;
    parts.push(`${cycles_after_cliff} ${unitCycle || ''} post-cliff cycle`);
  }

  // Vesting period
  if (period_of_vesting) {
    const unitVesting =
      unit_of_vesting_period && parseInt(period_of_vesting) > 1
        ? `${unit_of_vesting_period}s`
        : unit_of_vesting_period;
    parts.push(`${period_of_vesting} ${unitVesting || ''} vesting period`);
  }

  return parts.length ? parts.join(', ') : undefined;
};

interface OfferFile {
  url?: string;
  content_type?: string;
  filename?: string;
}

// The document list to show for an offer. For e-sign the BE consolidates all
// offer template files into a single signed offer once signed — from then on show
// only the signed offer + additional attachments; the individual template PDFs/DOCX
// stay under the Files tab (CORE-4794). Before signing, show the generated docs
// (multi-doc array, else the single PDF) plus attachments.
export const getOfferDocumentFiles = (offer?: {
  signed_offer_document_pdf?: OfferFile;
  offer_documents_pdf?: OfferFile[];
  offer_document_pdf?: OfferFile;
  attachments?: OfferFile[];
}): OfferFile[] => {
  const {
    signed_offer_document_pdf,
    offer_documents_pdf,
    offer_document_pdf,
    attachments,
  } = offer || {};

  const attachmentFiles = (
    Array.isArray(attachments) ? attachments : []
  ).filter((file) => file?.url);

  if (signed_offer_document_pdf?.url) {
    return [signed_offer_document_pdf, ...attachmentFiles];
  }

  const generatedDocs = offer_documents_pdf?.length
    ? offer_documents_pdf.filter((doc) => doc?.url)
    : offer_document_pdf?.url
      ? [offer_document_pdf]
      : [];

  return [...generatedDocs, ...attachmentFiles];
};
